import { useState } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";
import {
  useQuery,
  keepPreviousData,
  useQueryClient,
  useMutation,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import Image from "../../elements/Image";
import Button from "../../form/Button";
import OrganizerEventCard from "../../items/OrganizerEventCard";

import { useSelector } from "react-redux";
import { eventList } from "../../../services/eventService";
import { lngDetect } from "../../../helpers/lngDetect";
import { useDebounce } from "../../../hooks/useDebounce";
import dayjs from "dayjs";
import { wishListManage } from "../../../services/wishlistService";
import { ShareButtons } from "../../../util/Flag";
import { classNames } from "../../../helpers/classNames";
import Search from "../../form/Search";
import DatePicker from "../../form/DatePicker";
import Select from "../../form/select/Select";
import ContactModal from "../../common/ContactModal";
import { PrivacyModal } from "../../common/PrivacyModal";
import {
  organizationFollow,
  organizationUnfollow,
} from "../../../services/followService";
import NoData from "../../elements/Nodata";
import noDataImage from "../../../assets/images/dance.svg";
import { t } from "i18next";

const getOrganizerStyles = (data) => {
  return {
    basicStyle: data?.basicStyle || null,
    customFonts: data?.customFonts || null,
    infoStyle: data?.infoStyle || null,
    headerStyle: data?.headerStyle || null,
    searchBarStyle: data?.searchBarStyle || null,
  };
};

const OrganizerDetail = () => {
  const queryClient = useQueryClient();
  const { data: organizationData } = useSelector(
    (state) => state?.organization?.selectedOrganizationDetails
  );
  const { session } = useSelector((state) => state?.authProvider);
  const { organizer: organizerData, organizationRefetch } = useOutletContext();
  const location = useLocation();
  const { lang } = useSelector((state) => state?.langProvider);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState({
    isOpen: false,
    data: {},
  });
  const [filters, setFilters] = useState({
    limit: 8,
    page: 1,
    sortQuery: "startDate",
  });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);

  const { basicStyle, headerStyle, searchBarStyle, infoStyle } =
    getOrganizerStyles(organizerData?.data?.themeSetting?.styles);

  const {
    data: eventsData,
    isLoading: eventLoading,
    isFetching: eventFetching,
  } = useQuery({
    queryKey: ["Organization-Event", filters, organizerData?.data?._id, search],
    queryFn: async () => {
      const filterData = { ...filters };
      const res = await eventList({
        ...filterData,
        organization: organizerData.data._id,
        startDate: new Date(),
        ...(search && { keyword: search }),
      });
      if (res?.docs?.status !== 200) {
        toast.warning(res?.message);
      }

      if (res?.docs?.status === 200) {
        const newDataArr = res?.docs?.data?.map((item) => {
          const date = item?.startDate
            ? dayjs(item?.startDate).format("DD MMM")
            : "";
          const endDate = item?.endDate
            ? " - " + dayjs(item?.endDate).format("DD MMM YYYY")
            : "";

          return {
            ...item,
            id: item?._id,
            date: `${date}${endDate}` || "",
            eventTitle: lngDetect(item?.title),
            location: item?.address?.name,
            image: item?.gallery?.map((img) => img?.url) || [],
            link: `/event-details/${item?.uid}`,
            lang,
            participants: item?.totalParticipant,
          };
        });
        if (search) {
          return {
            ...res?.docs,
            data: [...newDataArr],
          };
        } else {
          return {
            ...res?.docs,
            data: [...(eventsData?.data || []), ...newDataArr]?.filter(
              (item, index, self) =>
                index === self.findIndex((t) => t?._id === item?._id)
            ),
          };
        }
      } else {
        return {
          ...res,
          data: [],
        };
      }
    },
    enabled: !!organizerData?.data?._id,
    placeholderData: keepPreviousData,
  });

  const loadMore = () => {
    if (eventsData?.metadata?.hasNextPage) {
      setFilters((pre) => ({
        ...pre,
        page: eventsData?.metadata?.nextPage,
      }));
    }
  };

  const { mutate: addToFavorite } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        referenceId: val?._id,
        organization: organizationData?._id,
        ...(val?.type && { type: val?.type }),
      };

      const res = await wishListManage(payload);
      return { res, ...(val?.type && { type: val?.type }) };
    },
    onSuccess: ({ res, type }) => {
      if (res?.status === 200) {
        if (type === "wishlist-organization") {
          queryClient.setQueryData(
            ["event-organizer", organizerData?.data?.uid],
            (state) => {
              const state_data = JSON.parse(JSON.stringify(state || {}));
              state_data.data.isWishlist = res?.data?.isWishlist;
              return { ...state_data };
            }
          );
        } else {
          queryClient.setQueryData(
            ["Organization-Event", filters, organizerData?.data?._id, search],
            (state) => {
              const state_data = JSON.parse(JSON.stringify(state || {}));
              const new_arr = state_data?.data?.map((item) =>
                item?._id === res?.data?._id ? { ...item, ...res?.data } : item
              );
              return { ...state_data, data: new_arr };
            }
          );
        }
      }
    },
  });

  const { mutate: manageFollowHandler, isPending } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        fromId: session?.user?._id,
        toId: organizerData?.data?._id,
        referenceType: "organization",
      };
      if (organizerData?.data?.isFollowing) {
        const res = await organizationUnfollow(payload);
        return res;
      } else {
        const res = await organizationFollow(payload);
        return res;
      }
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        queryClient.setQueryData(
          ["event-organizer", organizerData?.data?.uid],
          (state) => {
            const state_data = JSON.parse(JSON.stringify(state || {}));
            state_data.data.isFollowing = res?.data?.status !== "cancelled";
            return { ...state_data };
          }
        );
        toast.success(res?.message);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <section
      className="pt-10 pb-20 bg-magnolia"
      {...(basicStyle &&
        Object.keys(basicStyle)?.length > 0 && {
          style: { backgroundColor: basicStyle?.background },
        })}
    >
      <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl w-full mx-auto space-y-6 px-8 xl:px-0">
        <div
          {...(headerStyle &&
            Object.keys(headerStyle)?.length > 0 && {
              style: { backgroundColor: headerStyle?.background },
            })}
          className="px-3 py-5 rounded-lg"
        >
          <div className="flex gap-5 text-sm">
            {organizerData?.data?.themeSetting?.headerLink?.map((link, i) =>
              link?.isVisible ? (
                <Link
                  to={link?.url || "#"}
                  target="_blank"
                  className="text-deep-purple font-medium text-lg cursor-pointer hover:opacity-70 transition-all duration-300"
                  {...(headerStyle &&
                    Object.keys(headerStyle)?.length > 0 && {
                      style: { color: headerStyle?.text },
                    })}
                  key={i?.toString()}
                >
                  {lngDetect(link?.label)}
                </Link>
              ) : (
                <></>
              )
            )}
          </div>
        </div>
        <div className="grid grid-cols-12 gap-5 3xl:gap-10">
          <div className="col-span-12 md:col-span-7 lg:col-span-4 2xl:col-span-3">
            <div
              className="sticky top-28 space-y-4 bg-sltae-100/10 rounded-lg px-4 py-5 overflow-hidden"
              style={{ backgroundColor: infoStyle?.background }}
            >
              {/* Favourite Button */}
              <div className="absolute right-3 top-3 z-[4]">
                <Button
                  buttonFunction={() => {
                    addToFavorite({
                      _id: organizerData?.data?._id,
                      type: "wishlist-organization",
                    });
                  }}
                  buttonIcon={
                    organizerData?.data?.isWishlist
                      ? "fa-solid fa-heart"
                      : "fa-regular fa-heart"
                  }
                  buttonIconPosition="left"
                  buttonClasses="w-9 aspect-square bg-white rounded-full flex items-center justify-center text-gradient-red"
                />
              </div>
              <div className="w-full rounded-md overflow-hidden border border-primary bg-black mb-5 aspect-[5/3]">
                <Image
                  src={organizerData?.data?.logo?.url}
                  alt={""}
                  classes="object-cover"
                />
              </div>
              <div className="space-y-2">
                <div
                  className="text-sm xl:text-xl 2xl:text-2xl font-bold uppercase text-deep-purple"
                  {...(infoStyle &&
                    Object.keys(infoStyle)?.length > 0 && {
                      style: { color: infoStyle?.text },
                    })}
                >
                  {organizerData?.data?.eventSetting?.title?.[lang] ||
                    organizerData?.data?.name?.[lang] ||
                    ""}
                </div>
                <div
                  className="text-[15px] font-normal text-deep-purple w-full !mb-5"
                  {...(infoStyle &&
                    Object.keys(infoStyle)?.length > 0 && {
                      style: { color: infoStyle?.text },
                    })}
                >
                  {organizerData?.data?.eventSetting?.description?.[lang] || ""}
                </div>

                <div className="relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-gradient-to-r after:from-purple-500 after:to-orange-500"></div>

                <div className="flex items-center justify-between !mt-10">
                  <h3
                    {...(infoStyle &&
                      Object.keys(infoStyle)?.length > 0 && {
                        style: { color: infoStyle?.text },
                      })}
                    className="text-base capitalize font-medium text-deep-purple flex items-center gap-2"
                  >
                    <span className="text-xs flex items-center justify-center flex-shrink-0 text-purple-600">
                      <i className="fa-solid fa-objects-column"></i>
                    </span>{" "}
                    {organizerData?.data?.name?.[lang] ?? ""}
                  </h3>
                  <h4
                    className="text-base capitalize font-medium text-deep-purple flex items-center gap-2"
                    {...(infoStyle &&
                      Object.keys(infoStyle)?.length > 0 && {
                        style: { color: infoStyle?.text },
                      })}
                  >
                    <span className="text-xs flex items-center justify-center flex-shrink-0 text-purple-600">
                      <i className="fa-solid fa-calendar-days"></i>
                    </span>{" "}
                    <span>{eventsData?.metadata?.totalDocs} </span>
                    {eventsData?.metadata?.totalDocs === 1 ? "event" : "events"}
                  </h4>
                </div>

                <div
                  className="text-base capitalize font-medium text-deep-purple flex items-center gap-2"
                  {...(infoStyle &&
                    Object.keys(infoStyle)?.length > 0 && {
                      style: { color: infoStyle?.text },
                    })}
                >
                  <span className="text-xs flex items-center justify-center flex-shrink-0 text-purple-600">
                    <i className="fa-solid fa-user"></i>
                  </span>{" "}
                  {organizerData?.data?.userData?.firstName ?? ""}{" "}
                  {organizerData?.data?.userData?.lastName ?? ""}
                </div>

                <div className="flex items-center gap-3 !mt-8 !mb-4">
                  <Button
                    buttonFunction={() => setIsModalOpen(true)}
                    buttonLabel={t("Contact")}
                    buttonIcon={"fa-light fa-paper-plane"}
                    buttonIconPosition="left"
                    buttonClasses={"bg-transparent text-deep-purple !p-0 h-0"}
                    buttonLabelClasses={"text-sm capitalize"}
                    buttonIconClass={"!text-sm"}
                    {...(infoStyle &&
                      Object.keys(infoStyle)?.length > 0 && {
                        style: { color: infoStyle?.text },
                      })}
                  />
                  <ContactModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    event={{ organizationData: organizerData?.data }}
                  />

                  <ShareButtons
                    url={`${window.location?.origin}${location?.pathname}`}
                    text="Join this amazing event:-"
                    buttonIcon={"fa-light fa-share-nodes"}
                    buttonClass={
                      "relative flex border-none justify-center items-center gap-2 text-deep-purple hover:bg-transparent font-semibold text-base h-12 px-6 rounded-full transition-all duration-300 !w-fit !p-0"
                    }
                    buttonLabel={t("share")}
                    buttonLabelClasses={"!text-sm !capitalize"}
                    buttonIconClass={"!text-sm"}
                    {...(infoStyle &&
                      Object.keys(infoStyle)?.length > 0 && {
                        style: { color: infoStyle?.text },
                      })}
                  />

                  {lngDetect(organizationData?.setting?.privacy) && (
                    <Button
                      buttonFunction={(prev) =>
                        setIsPrivacyModalOpen({
                          ...prev,
                          isOpen: true,
                          data: organizerData?.data,
                        })
                      }
                      buttonClasses={
                        "text-deep-purple transition-all duration-300 font-medium text-sm capitalize bg-transparent p-0 h-0"
                      }
                      buttonLabel={t("Privacy & Policy")}
                      buttonIcon={"fa-fw fa-light fa-shield-halved"}
                      buttonIconPosition={"left"}
                      buttonIconClass={"!text-sm"}
                      buttonLabelClasses={"!text-sm !capitalize"}
                      {...(infoStyle &&
                        Object.keys(infoStyle)?.length > 0 && {
                          style: { color: infoStyle?.text },
                        })}
                    />
                  )}

                  <PrivacyModal
                    isOpen={isPrivacyModalOpen?.isOpen}
                    onClose={(prev) =>
                      setIsPrivacyModalOpen({
                        ...prev,
                        isOpen: false,
                      })
                    }
                    organizationData={isPrivacyModalOpen?.data}
                  />
                </div>

                <div className="flex items-center gap-5">
                  {/* <Button
                  buttonLabel={"1Follower"}
                  buttonIcon={"fa-light fa-heart"}
                  buttonIconPosition="left"
                  buttonClasses={"bg-secondary-gradient hover:opacity-90"}
                /> */}
                  {/* <Button
                  buttonIcon={"fa-light fa-share-nodes"}
                  buttonIconPosition="left"
                  buttonClasses={"bg-secondary-gradient hover:opacity-90"}
                  buttonLabel={"share"}
                /> */}
                </div>
              </div>

              {/* <div className="text-base capitalize font-medium text-deep-purple flex items-center gap-3">
                <span className="bg-primary/10 w-8 h-8 text-sm border border-primary flex items-center justify-center rounded-md flex-shrink-0">
                  <i className="fa-solid fa-briefcase"></i>
                </span>{" "}
                Professional Dance Event Organizer, Creating unforgettable dance experiences
              </div> */}
              {/* <div className="text-base capitalize font-medium text-deep-purple flex items-center gap-3">
                <span className="bg-primary/10 w-8 h-8 text-sm border border-primary flex items-center justify-center rounded-md flex-shrink-0">
                  <i className="fa-solid fa-location-dot"></i>
                </span>{" "}
                Los Angeles, CA
              </div> */}
              <Button
                buttonLabel={t(
                  !organizerData?.data?.isFollowing
                    ? isPending
                      ? "Following"
                      : "Follow"
                    : isPending
                    ? "Unfollowing"
                    : "Unfollow"
                )}
                buttonIcon={
                  !organizerData?.data?.isFollowing
                    ? "fa-light fa-user-plus"
                    : "fa-light fa-user-minus"
                }
                buttonIconClass={"text-deep-purple"}
                buttonClasses={"w-full bg-transparent border-deep-purple mt-6"}
                buttonLabelClasses={"text-deep-purple"}
                buttonFunction={(e) => {
                  e.stopPropagation();
                  manageFollowHandler();
                }}
                {...(infoStyle &&
                  Object.keys(infoStyle)?.length > 0 && {
                    style: {
                      backgroundColor: infoStyle?.buttonColour,
                      color: infoStyle?.buttonText,
                      ...(infoStyle?.buttonColour && {
                        backgroundImage: "none",
                      }),
                    },
                  })}
                {...(isPending && {
                  loading: true,
                  buttonIconPosition: "left",
                  isDisable: true,
                })}
              />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-8 2xl:col-span-9">
            <div className="lg:border-l lg:border-slate-200 lg:pl-5 2xl:pl-7">
              <div className="flex items-center justify-between mb-8">
                <div
                  className="text-deep-purple capitalize font-semibold text-2xl"
                  {...(basicStyle &&
                    Object.keys(basicStyle)?.length > 0 && {
                      style: { color: basicStyle?.text },
                    })}
                >
                  Next events of {organizerData?.data?.name?.[lang] ?? ""}
                </div>
              </div>
              {/* <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 2xl:gap-8">
              {events.map((event) => (
                <OrganizerEventCard key={event.id} event={event} />
              ))}
            </div> */}

              {organizerData?.data?.eventSetting?.searchSortFilter &&
                organizerData?.data?.eventSetting?.searchSortFilter?.filter(
                  (item) => item !== "venue"
                )?.length > 0 && (
                  <div
                    {...(searchBarStyle &&
                      Object.keys(searchBarStyle)?.length > 0 && {
                        style: { backgroundColor: searchBarStyle?.background },
                      })}
                    className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-8 2xl:gap-52 bg-gray-100 py-4 rounded-lg px-3 border-l-2 border-primary mb-5"
                  >
                    <div className="w-full md:w-1/4">
                      {organizerData?.data?.eventSetting?.searchSortFilter.includes(
                        "search"
                      ) && (
                        <Search
                          searchValue={keyword}
                          placeholder={t("Search here")}
                          search={setKeyword}
                          divClasses={
                            "!border-surface-05 mb-0 !bg-surface-04 !h-10 !font-medium !text-deep-purple !rounded-md"
                          }
                          inputClasses={"!text-deep-purple"}
                        />
                      )}
                    </div>

                    <div className="w-full md:w-1/2">
                      <div className="flex flex-col md:flex-row items-center justify-end gap-3 md:gap-2 2xl:gap-5">
                        {organizerData?.data?.eventSetting?.searchSortFilter.includes(
                          "date"
                        ) && (
                          <DatePicker
                            range
                            onChange={(val) => {
                              setFilters((pre) => ({ ...pre, ...val }));
                            }}
                            inputClassName={"!bg-transparent"}
                          />
                        )}
                        {organizerData?.data?.eventSetting?.searchSortFilter.includes(
                          "sort"
                        ) && (
                          <Select
                            placeholder={t("Sort by")}
                            dropdownData={[
                              {
                                name: (
                                  <div className="flex items-center gap-2">
                                    <span>
                                      <i className="fa-solid fa-arrow-down-a-z"></i>
                                    </span>
                                    <span>Name</span>
                                  </div>
                                ),
                                value: `title[${lang}]`,
                              },
                              {
                                name: (
                                  <div className="flex items-center gap-2">
                                    <span>
                                      <i className="fa-solid fa-arrow-down-z-a"></i>
                                    </span>
                                    <span>Name</span>
                                  </div>
                                ),
                                value: `-title[${lang}]`,
                              },
                              {
                                name: (
                                  <div className="flex items-center gap-2">
                                    <span>
                                      <i className="fa-solid fa-arrow-down-a-z"></i>
                                    </span>
                                    <span>Start Date</span>
                                  </div>
                                ),
                                value: "startDate",
                              },
                              {
                                name: (
                                  <div className="flex items-center gap-2">
                                    <span>
                                      <i className="fa-solid fa-arrow-down-z-a"></i>
                                    </span>
                                    <span>Start Date</span>
                                  </div>
                                ),
                                value: "-startDate",
                              },
                            ]}
                            selectedValue={filters?.sortQuery}
                            onChange={(val) => {
                              setFilters((pre) => ({
                                ...pre,
                                sortQuery: val,
                              }));
                            }}
                            dropdownButtonClass={
                              "!border-surface-05 !bg-surface-04 !h-10 !font-medium !text-deep-purple !rounded-md"
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              {eventsData?.data?.length > 0 ? (
                <div
                  className={classNames(
                    organizerData?.data?.eventSetting?.listLayout === "grid"
                      ? "grid md:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3 gap-2 2xl:gap-8"
                      : "flex flex-col gap-3"
                  )}
                >
                  {eventsData?.data.map((event) => (
                    <OrganizerEventCard
                      key={event?.id || event?._id}
                      event={event}
                      addToFavorite={addToFavorite}
                      layoutStyle={
                        organizerData?.data?.eventSetting?.listLayout || "grid"
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="pt-10 mt-10 h-full my-auto">
                  <NoData image={noDataImage} message="No events found." />
                </div>
              )}
              {eventsData?.data?.length > 0 &&
                eventsData?.data?.length < eventsData?.metadata?.totalDocs && (
                  <div className="flex flex-col space-y-4">
                    <div className="text-deep-purple text-xl 2xl:text-2xl font-semibold text-center">
                      Showing {eventsData?.data?.length} of{" "}
                      {eventsData?.metadata?.totalDocs}
                    </div>
                    {eventsData?.metadata?.hasNextPage && (
                      <div className="flex justify-center">
                        <Button
                          buttonLabel="Load More"
                          buttonLabelClasses="text-lg uppercase text-white"
                          buttonClasses="h-14 bg-secondary-gradient"
                          buttonFunction={loadMore}
                          {...(eventFetching && {
                            loading: true,
                            buttonIconPosition: "left",
                            isDisable: true,
                          })}
                        />
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrganizerDetail;
